import React, { useContext } from 'react';
import { FormattedCurrency } from '@qover/react-intl';
// @ts-ignore
import { ThemeContext } from '@qover/styles';
import {
  Footnote,
  Sitemap,
  Caption,
  Heading3,
  Heading4,
} from '@qover/typography';
import { roundUpCurrency } from '@qover/utils/src/tools';
import { DiscountForm } from '../forms';
import { TicketIcon } from '@qover/icons/TicketIcon';
import CoverageIcon from './CoverageIcon';
import {
  CartContainer,
  CartHeader,
  Divider,
  Footer,
  OptionList,
  Price,
  TotalPrice,
  DiscountContainer,
  DiscountInnerContainer,
  DiscountPopover,
} from './coverageSection.styles';
import messages from './coverageSection.messages';
import { Popover } from '@qover/molecules';

export enum PriceFrequencyPosition {
  BOTTOM = 'BOTTOM',
  RIGHT = 'RIGHT',
}

type CoverageProps = {
  addDiscount?: (discountCode: string) => Promise<void>;
  basePrice?: number;
  basePriceFrequency?: React.ReactNode;
  cashbackMessage?: React.ReactNode;
  conditionsMessage?: React.ReactNode;
  discountCode?: string;
  discountFirstYearOnly?: boolean;
  discountFirstYearOnlyMessage?: JSX.Element | boolean;
  discountInformations?: JSX.Element | boolean;
  options?: Array<{
    label: string | React.ReactNode;
    value: number | string;
    'data-test': string;
  }>;
  optionsTitle?: React.ReactNode;
  productPrice?: number;
  productPriceFrequency?: React.ReactNode;
  recurringDiscount?: boolean;
  removeDiscount?: (discountCode: string) => Promise<void>;
  renderIcon?: React.FC;
  secondaryPrice?: number;
  secondaryPriceFrequency?: React.ReactNode;
  title: React.ReactNode;
  totalPrice: number;
  totalPriceFrequency: React.ReactNode;
  totalPriceFrequencyPosition?: PriceFrequencyPosition;
};

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    viewBox="0 0 10 10"
  >
    <defs>
      <path
        id="discount-a"
        d="M9.708.292c.365.366.388.945.068 1.337l-.068.076L6.412 5l3.296 3.295c.367.367.388.949.064 1.341l-.064.072c-.366.365-.945.388-1.337.068l-.076-.068L5 6.412 1.705 9.708c-.367.367-.949.388-1.341.064l-.072-.064c-.365-.366-.388-.945-.068-1.337l.068-.076L3.588 5 .292 1.705C-.075 1.338-.096.756.228.364L.292.292c.366-.365.945-.388 1.337-.068l.076.068L5 3.588 8.295.292c.367-.367.949-.388 1.341-.064l.072.064z"
      />
    </defs>
    <g fillRule="evenodd">
      <mask id="discount-b">
        <use xlinkHref="#discount-a" />
      </mask>
      <use fillRule="nonzero" xlinkHref="#discount-a" />
      <g fillRule="nonzero" mask="url(#discount-b)">
        <path d="M0 0H10V10H0z" />
      </g>
    </g>
  </svg>
);

export function CoverageSection(props: CoverageProps) {
  const { totalPriceFrequencyPosition = PriceFrequencyPosition.BOTTOM } = props;
  const theme = useContext<any>(ThemeContext);
  const RenderIcon = props.renderIcon ?? CoverageIcon;

  const asterisk =
    !props.recurringDiscount && props.discountFirstYearOnly ? '*' : null;

  const { cashback, showDiscount } = theme?.featureToggles ?? {};

  return (
    <CartContainer>
      <CartHeader>
        <div>
          <RenderIcon />
          <span className="bold">{props.title}</span>
        </div>
        {!!props.productPrice && (
          <Price data-test="general.summaryCard.price">
            <FormattedCurrency value={props.productPrice} />
            {props.productPriceFrequency}
          </Price>
        )}
      </CartHeader>
      {!!props.options?.length && (
        <>
          <Divider />
          {props.optionsTitle && (
            <Caption as="div" className="upper">
              {props.optionsTitle}
            </Caption>
          )}
          <OptionList>
            {props.options.map((option, idx) => (
              <li key={idx}>
                <Sitemap as="div" className="bold">
                  {option.label}
                </Sitemap>
                {option.value && (
                  <span data-test={option['data-test']}>
                    {typeof option.value === 'string' && option.value}
                    {typeof option.value === 'number' && (
                      <FormattedCurrency
                        value={roundUpCurrency(option.value)}
                      />
                    )}
                  </span>
                )}
              </li>
            ))}
          </OptionList>
        </>
      )}
      {(showDiscount || cashback) && (
        <DiscountContainer>
          <DiscountForm
            actionContainerClassName="my-0"
            addDiscount={props.addDiscount}
            discountCode={props.discountCode}
            removeDiscount={props.removeDiscount}
            small
          >
            <DiscountInnerContainer>
              <span className="font-bold">
                <TicketIcon />
                {props.discountCode}
              </span>
              {props.removeDiscount && (
                <button
                  onClick={() => {
                    // @ts-ignore
                    props.removeDiscount(props.discountCode);
                  }}
                  type="button"
                >
                  <CloseIcon />
                </button>
              )}
            </DiscountInnerContainer>
          </DiscountForm>
          {props.discountInformations && (
            <Popover
              button={
                <DiscountPopover>{messages.discountPopover}</DiscountPopover>
              }
            >
              {props.discountInformations}
            </Popover>
          )}
        </DiscountContainer>
      )}
      <Divider />
      <Footer>
        <Heading3 as="div">{messages.total}</Heading3>
        <TotalPrice>
          <Heading4 as="div" data-test="general.summaryCard.total.price">
            <FormattedCurrency value={props.totalPrice} />
            {totalPriceFrequencyPosition === PriceFrequencyPosition.RIGHT && (
              <Footnote>{props.totalPriceFrequency}</Footnote>
            )}
            {asterisk}
          </Heading4>
          {totalPriceFrequencyPosition === PriceFrequencyPosition.BOTTOM && (
            <Footnote>
              {props.totalPriceFrequency}
              {asterisk}
            </Footnote>
          )}
          {!!props.secondaryPrice && (
            <div>
              <Sitemap
                as="div"
                className="bold"
                data-test="general.summaryCard.total.subprice"
              >
                <FormattedCurrency
                  value={roundUpCurrency(props.secondaryPrice)}
                />{' '}
                {props.secondaryPriceFrequency}
                {asterisk}
              </Sitemap>
            </div>
          )}
          {!!props.basePrice && (
            <div>
              <Sitemap
                className="bold"
                data-test="general.summaryCard.total.basePrice"
                style={{ textDecoration: 'line-through' }}
              >
                <FormattedCurrency value={roundUpCurrency(props.basePrice)} />
                {props.basePriceFrequency}
              </Sitemap>
            </div>
          )}
          {!props.recurringDiscount && !!props.discountFirstYearOnly && (
            <div style={{ marginTop: '5px' }}>
              <Sitemap as="div" data-test="general.summaryCard.total.subprice">
                {props.discountFirstYearOnlyMessage}
              </Sitemap>
            </div>
          )}
          {cashback && props.cashbackMessage && (
            <div style={{ marginTop: '5px' }}>
              <Sitemap
                as="div"
                className="color-primary font-bold"
                data-test="general.summaryCard.total.cashback"
              >
                {props.cashbackMessage}
              </Sitemap>
            </div>
          )}
          {(cashback || showDiscount) && props.conditionsMessage && (
            <div style={{ marginTop: '5px' }}>
              <Sitemap
                as="div"
                data-test="general.summaryCard.total.conditions"
              >
                {props.conditionsMessage}
              </Sitemap>
            </div>
          )}
        </TotalPrice>
      </Footer>
    </CartContainer>
  );
}
